import { Component, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatBadgeModule } from '@angular/material/badge';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableModule } from '@angular/material/table';
import { DataSourceOrdenacaoInsensivel } from '@shared/helpers/ordenacao-tabela.helper';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SelectionModel } from '@angular/cdk/collections';
import { environment } from '../../../../../../../environments/environment';
import { SelectPesquisavelComponent } from '@shared/components/select-pesquisavel/select-pesquisavel.component';
import { AlertService, AlertType } from '@shared/services/alert.service';
import { RegraTributariaService } from '@shared/services/apis/motor-fiscal/regra-tributaria.service';
import { RegraTributariaNaturezaOperacaoStruct } from '@shared/services/structs/motor-fiscal/regra-tributaria-natureza-operacao.struct';
import { MotorFiscalLookupService } from '@shared/services/apis/motor-fiscal/motor-fiscal-lookup.service';
import { ModeloDocumentoService } from '@shared/services/apis/dfe/modelo-documento.service';
import { TipoEscopoEnum } from '@shared/enums/motor-fiscal/tipo-escopo.enum';
import { RegraTributariaErrorCodeEnum } from '@shared/enums/motor-fiscal/regra-tributaria-error-code.enum';
import { EmptyListComponent } from '@shared/components/empty-list/empty-list.component';
import { ConfirmModalComponent } from '@shared/components/confirm-modal/confirm-modal.component';
import { LoadingBoxDefaultListComponent } from '@shared/components/loading-box-default-list/loading-box-default-list.component';
import { ModeloDocumento } from '@shared/services/models/dfe/modelo-documento.model';
import { ModeloDocumentoLookupResponse } from '@shared/services/responses/dfe/modelo-documento-lookup.response';
import { ModeloDocumentoEnum } from '@shared/enums/dfe/modelo-documento.enum';

@Component({
    selector: 'app-regras-lista',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        MatButtonModule,
        MatCheckboxModule,
        MatFormFieldModule,
        MatIconModule,
        MatInputModule,
        MatMenuModule,
        MatSelectModule,
        MatBadgeModule,
        MatProgressSpinnerModule,
        MatSortModule,
        MatTableModule,
        MatDialogModule,
        MatTooltipModule,
        EmptyListComponent,
        LoadingBoxDefaultListComponent,
        SelectPesquisavelComponent,
    ],
    templateUrl: './regras-lista.component.html',
    styleUrl: './regras-lista.component.css',
})
export class RegrasListaComponent implements OnInit {
    public model: FormGroup;
    
    public _paginaAtual: number = 1;
    public _tamanhoPagina: number = 20;
    public _totalRegistros: number = 0;

    public _dataSource = new DataSourceOrdenacaoInsensivel<RegraTributariaNaturezaOperacaoStruct>();
    public _allData: RegraTributariaNaturezaOperacaoStruct[] = [];
    public _isLoading: boolean = false;
    
    public qtdFiltrosAvancados: number = 0;

    public readonly naturezaOperacaoLookupUrl = environment.urlApiMotorFiscal + 'Lookup/natureza-operacao';
    public modelosDocumento: ModeloDocumento[] = [];
    // Modelo de documento pertence ao db_dfe: as regras guardam apenas o codigo; aqui mapeamos codigo -> nome.
    public nomeModeloPorCodigo: Record<string, string> = {};
    public tiposEscopoLabels: Record<number, string> = {};

    public displayedColumns: string[] = [
        'select', 'nome', 'modelos', 'cst', 'cclassTrib', 'escopos', 'status', 'actions'
    ];

    public selection = new SelectionModel<RegraTributariaNaturezaOperacaoStruct>(true, []);

    @ViewChild(MatSort) set matSort(sort: MatSort) {
        this._dataSource.sort = sort;
    }

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private regraService: RegraTributariaService,
        private motorFiscalLookupService: MotorFiscalLookupService,
        private alertService: AlertService,
        private dialog: MatDialog,
        private modeloDocumentoService: ModeloDocumentoService
    ) {}

    ngOnInit(): void {
        this.model = this.fb.group({
            pesquisa: [''],
            modeloDocumento: [null],
            naturezaOperacao: [null]
        });
        this._isLoading = true;
        this.carregarModelosDocumento();
        this.carregarTiposEscopo();
    }

    private carregarModelosDocumento(): void {
        this.modeloDocumentoService.listar().subscribe({
            next: (res) => {
                const modelos = res.modelos ?? [];
                this.nomeModeloPorCodigo = {};
                for (const m of modelos) {
                    if (m.codigo) {
                        this.nomeModeloPorCodigo[m.codigo] = m.nome || m.descricao || m.codigo;
                    }
                }
                this.modelosDocumento = modelos.filter(modelo =>
                    modelo.idModeloDocumento === ModeloDocumentoEnum.NFe ||
                    modelo.idModeloDocumento === ModeloDocumentoEnum.NFCe,
                );
                this.carregarRegras(true);
            },
            error: (err) => {
                this.carregarRegras(true);
                console.error('Erro ao carregar modelos de documento', err);
            }
        });
    }

    public getNomeModeloDocumento(codigo: string): string {
        return this.nomeModeloPorCodigo[codigo] ?? 'Modelo indisponível';
    }

    private carregarTiposEscopo(): void {
        this.motorFiscalLookupService.getTiposEscopo().subscribe({
            next: list => {
                this.tiposEscopoLabels = list.reduce((acc, item) => {
                    acc[item.id] = item.descricao;
                    return acc;
                }, {} as Record<number, string>);
            },
            error: (err) => {
                console.error('Erro ao carregar tipos de escopo', err);
            }
        });
    }

    public getTipoEscopoLabel(tipoEscopo: TipoEscopoEnum): string {
        return this.tiposEscopoLabels[tipoEscopo] || `Escopo ${tipoEscopo}`;
    }

    public getControl(name: string): FormControl {
        return this.model.get(name) as FormControl;
    }

    public carregarRegras(reset: boolean = false): void {
        this._isLoading = true;
        const pesquisa = this.model.get('pesquisa')?.value || null;
        const modeloDocumento = this.model.get('modeloDocumento')?.value || null;
        const naturezaOperacao = this.model.get('naturezaOperacao')?.value || null;

        // Count advanced filters
        let count = 0;
        if (modeloDocumento) count++;
        if (naturezaOperacao) count++;
        this.qtdFiltrosAvancados = count;

        this.regraService.getAll(pesquisa, modeloDocumento, naturezaOperacao, this._paginaAtual, this._tamanhoPagina).subscribe({
            next: response => {
                if (response.isError) {
                    // Empresa sem regras: cria as regras padrão e recarrega (mesmo molde da natureza e do DIFAL).
                    if (response.errorCode === RegraTributariaErrorCodeEnum.SeedPendente) {
                        this.inicializarRegras();
                        return;
                    }
                    this._isLoading = false;
                    this.alertService.show('Erro', response.errorDescription || 'Erro ao carregar regras tributárias', AlertType.error);
                    return;
                }
                this._isLoading = false;
                this._totalRegistros = response.totalRegistros;
                if (reset) {
                    this._allData = response.regras;
                    this.selection.clear();
                } else {
                    this._allData = [...this._allData, ...response.regras];
                }
                this._dataSource.data = this._allData;
            },
            error: err => {
                this._isLoading = false;
                this.alertService.show('Erro inesperado', err.message || 'Erro ao carregar regras tributárias', AlertType.error);
            },
        });
    }

    /** Cria as regras tributárias padrão da empresa e recarrega a listagem. */
    private inicializarRegras(): void {
        this.alertService.show('Informação', 'Carregando as configurações base...', AlertType.info);
        this.regraService.seed().subscribe({
            next: seed => {
                if (seed.isError) {
                    this._isLoading = false;
                    this.alertService.show('Erro', seed.errorDescription || 'Erro ao inicializar as regras tributárias', AlertType.error);
                    return;
                }
                this._paginaAtual = 1;
                this.carregarRegras(true);
            },
            error: err => {
                this._isLoading = false;
                this.alertService.show('Erro inesperado', err.message || 'Erro ao inicializar as regras tributárias', AlertType.error);
            },
        });
    }

    public clickPesquisar(): void {
        this._paginaAtual = 1;
        this.carregarRegras(true);
    }

    public aplicarFiltros(): void {
        this._paginaAtual = 1;
        this.carregarRegras(true);
    }

    public limparFiltros(): void {
        this.model.reset({
            pesquisa: '',
            modeloDocumento: null,
            naturezaOperacao: null
        });
        this.qtdFiltrosAvancados = 0;
        this._paginaAtual = 1;
        this.carregarRegras(true);
    }

    public proximaPagina(): void {
        this._paginaAtual++;
        this.carregarRegras();
    }

    public isAllSelected(): boolean {
        return this.selection.selected.length === this._dataSource.data.length && this._dataSource.data.length > 0;
    }

    public toggleAllRows(): void {
        if (this.isAllSelected()) {
            this.selection.clear();
        } else {
            this.selection.select(...this._dataSource.data);
        }
    }

    public editarRegra(id: number): void {
        this.router.navigate(['/configuracoes/reforma-tributaria/regras/cadastrar', id]);
    }

    public alternarStatus(item: RegraTributariaNaturezaOperacaoStruct): void {
        const novoStatus = !item.isAtivo;
        this.regraService.alterarStatus(item.idRegraTributaria, novoStatus).subscribe({
            next: response => {
                if (response.isError) {
                    this.alertService.show('Erro', response.errorDescription || 'Erro ao alterar o status', AlertType.error);
                    return;
                }
                item.isAtivo = novoStatus;
                // O status gera uma nova versão (id novo): recarrega para as próximas ações usarem a versão atual.
                this.carregarRegras();
                this.alertService.show('Sucesso', `Regra tributária ${novoStatus ? 'ativada' : 'inativada'} com sucesso.`, AlertType.success);
            },
            error: err => {
                this.alertService.show('Erro inesperado', err.message || 'Erro ao alterar o status', AlertType.error);
            },
        });
    }

    public confirmarExclusao(item: RegraTributariaNaturezaOperacaoStruct): void {
        const dialogRef = this.dialog.open(ConfirmModalComponent, {
            data: {
                title: 'Confirmar Exclusão',
                description: `Você tem certeza que deseja excluir a regra tributária "${item.nome}"? Esta ação não poderá ser desfeita.`,
                buttonConfirm: 'Confirmar',
                buttonClose: 'Cancelar',
            },
        });

        dialogRef.afterClosed().subscribe(result => {
            if (result && result.confirm) {
                this.excluirRegra(item.idRegraTributaria);
            }
        });
    }

    private excluirRegra(id: number): void {
        this.regraService.excluir(id).subscribe({
            next: response => {
                if (response.isError) {
                    this.alertService.show('Erro', response.errorDescription || 'Erro ao excluir', AlertType.error);
                    return;
                }
                this.alertService.show('Sucesso', 'Regra tributária excluída com sucesso.', AlertType.success);
                this.clickPesquisar();
            },
            error: err => {
                this.alertService.show('Erro inesperado', err.message || 'Erro ao excluir', AlertType.error);
            },
        });
    }
}
